/**
 * What a line IS — shown where it adds something, and not where it does not.
 *
 * The temptation is to print the whole classification everywhere, and it makes
 * a card worse: "Candle Lighting" beside "Hadlakas Neiros" is one fact in two
 * languages, and "Maariv & Megillah" beside "Maariv + Megillah" is one fact
 * twice. A label already names what happens at a line. So each surface takes
 * only the part its label is NOT already saying:
 *
 *  - `KindBadge` — that candle lighting is a reference time and not a minyan.
 *    Nothing else on a card says this.
 *  - `MomentNote` — that HaMelech is a point INSIDE shachris rather than a
 *    minyan of it. Nothing else says this either, and it is the difference
 *    between a list of minyanim being right and being wrong.
 *  - `Classification` — all of it, including `contains`, for the logic view,
 *    where the question being asked is what the engine thinks rather than what
 *    the sheet says.
 *
 * `contains` earns its place by being FILTERABLE, not by being printed: "every
 * mincha" is the thing that could not be asked before.
 */

import { type CalendarSpelling, spelled } from '@app/shared'

/** Vocabulary whose plain title case would be wrong or unidiomatic. */
const SPELLED: Record<string, string> = {
  kabbalas_shabbos: 'Kabbalas Shabbos',
  sefiras_haomer: "Sefiras Ha'Omer",
  sof_zman_krias_shema: 'Sof Zman Krias Shema',
  sof_zman_achilas_chametz: 'Sof Zman Achilas Chametz',
  sof_zman_biur_chametz: 'Sof Zman Biur Chametz',
  hadlakas_neiros: 'Hadlakas Neiros',
  krias_shema: 'Krias Shema',
  tekias_shofar: 'Tekias Shofar',
  hamelech: 'HaMelech',
  kol_nidrei: 'Kol Nidrei',
  neilas_hachag: 'Neilas HaChag',
  avos_ubanim: "Avos u'Banim",
  sale_of_aliyos: 'Sale of aliyos',
  daf_yomi: 'Daf Yomi',
  hakel: 'Hakel',
  tzeis: 'Tzeis',
}

/**
 * A vocabulary key as a person reads it, in the shul's spelling.
 *
 * Falls through to title case rather than to the raw key: the open half of the
 * vocabulary is whatever a shul says, so a word nobody has spelled here still
 * has to render as words. Only a word spelled here is respelled — a term the
 * shul typed is its own.
 */
export function termLabel(term: string, spelling: CalendarSpelling): string {
  const known = SPELLED[term]
  if (known) return spelled(known, spelling)
  return term
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export const KIND_LABEL: Record<string, string> = {
  tefilla: 'Tefilla',
  shiur: 'Shiur',
  event: 'Event',
  zman: 'Zman',
  info: 'Info',
}

export type Classified = {
  kind?: string | null
  contains?: string[] | null
  moment?: string | null
}

/**
 * What kind of line this is. A category, so it reads as a category — muted, on
 * a faint fill, like every other status in the system.
 */
export function KindBadge({ kind }: { kind?: string | null }) {
  if (!kind) return null
  return <span className="classify__kind">{KIND_LABEL[kind] ?? kind}</span>
}

/**
 * That this line is a point DURING something rather than the thing itself.
 *
 * Phrased as a relationship — "during Shachris" — because that is what it is,
 * and because a bare "HaMelech" beside a label already reading HaMelech would
 * say nothing at all.
 */
export function MomentNote({
  rule,
  spelling,
}: {
  rule: Classified
  spelling: CalendarSpelling
}) {
  if (!rule.moment) return null
  const within = (rule.contains ?? [])[0]
  return (
    <span className="classify__moment">
      during {within ? termLabel(within, spelling) : 'the tefilla'}
    </span>
  )
}

/**
 * The whole classification, for the logic view — where a reader is asking what
 * the engine believes and redundancy with the label is the point rather than
 * the problem.
 */
export function Classification({
  rule,
  spelling,
}: {
  rule: Classified
  spelling: CalendarSpelling
}) {
  const contains = rule.contains ?? []
  if (!rule.kind && !contains.length) return null
  return (
    <span className="classify">
      <KindBadge kind={rule.kind} />
      {contains.length ? (
        <span className="classify__of">
          {contains.map((t) => termLabel(t, spelling)).join(' + ')}
        </span>
      ) : null}
      <MomentNote rule={rule} spelling={spelling} />
    </span>
  )
}
